import { useMemo, useState } from "react";

interface Tile { id: number; ch: string }

/**
 * Kahoot-style spelling guesser: N blank boxes (word length is broadcast)
 * plus a compact tile bank — the word's own letters + 2-3 decoys, shuffled
 * server-side (see DrawRoom.buildLetterBank). Kept deliberately small (not
 * a full A-Z keyboard) so it's easier for young ESL learners AND leaves the
 * canvas much bigger on screen. Tapping a tile fills the next box and hides
 * that tile; tapping a filled box returns the tile to the bank.
 *
 * The parent should pass a `key` (e.g. round index + wrong-guess count) to
 * remount this component and clear the boxes after a wrong guess or a new
 * round — simplest way to reset local state cleanly.
 */
export function DrawGuessKeyboard({ wordLength, bank, onSubmit }: {
  wordLength: number;
  bank: string[];
  onSubmit: (text: string) => void;
}) {
  const tiles: Tile[] = useMemo(() => bank.map((ch, id) => ({ id, ch })), [bank]);
  const [placed, setPlaced] = useState<({ tileId: number; ch: string } | null)[]>(
    () => Array(Math.max(1, wordLength)).fill(null),
  );

  const usedIds = new Set(placed.filter(Boolean).map((p) => p!.tileId));
  const nextEmpty = placed.findIndex((p) => p == null);
  const full = nextEmpty === -1;
  const anyFilled = placed.some(Boolean);

  const tapTile = (t: Tile) => {
    if (full || usedIds.has(t.id)) return;
    setPlaced((prev) => prev.map((p, i) => (i === nextEmpty ? { tileId: t.id, ch: t.ch } : p)));
  };
  const tapBox = (i: number) => {
    if (!placed[i]) return;
    setPlaced((prev) => prev.map((p, j) => (j === i ? null : p)));
  };
  const clearAll = () => setPlaced(Array(Math.max(1, wordLength)).fill(null));
  const submit = () => { if (full) onSubmit(placed.map((p) => p!.ch).join("")); };

  return (
    <div className="drawit-kb">
      <div className="drawit-kb-boxes">
        {placed.map((p, i) => (
          <button key={i} className={`drawit-kb-box ${p ? "filled" : ""}`} onClick={() => tapBox(i)} aria-label={p ? `Remove letter ${p.ch}` : "Empty letter"}>
            {p?.ch ?? ""}
          </button>
        ))}
      </div>
      <div className="drawit-kb-bank">
        {tiles.map((t) => (
          <button
            key={t.id}
            className="drawit-kb-tile"
            onClick={() => tapTile(t)}
            disabled={usedIds.has(t.id) || full}
            style={{ visibility: usedIds.has(t.id) ? "hidden" : "visible" }}
          >
            {t.ch}
          </button>
        ))}
      </div>
      <div className="drawit-kb-actions">
        <button className="btn btn-ghost" onClick={clearAll} disabled={!anyFilled}>Clear</button>
        <button className="btn btn-green" onClick={submit} disabled={!full}>Guess</button>
      </div>
    </div>
  );
}
